import { gql, useQuery } from "@apollo/client";
import {
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Typography,
} from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import cx from "clsx";
import { GetLinkPreview, GetLinkPreviewVariables } from "generated/schemaTypes";
import { useTrackEngagement } from "react-context/legacy/TrackContentModuleEngagementContextProvider";
import useAnalytics from "react-hooks/useAnalytics";

import styles from "../Links.module.scss";

const PRISMA_GET_LINK_PREVIEW = gql`
  query GetLinkPreview($url: String!) {
    getLinkPreview(url: $url) {
      title
      description
      favicon
      image
      url
    }
  }
`;

function LinkLoading() {
  return (
    <div className={cx(styles.loading)}>
      <Skeleton variant="rect" width={245} height={140} />
      <Skeleton variant="text" />
      <Skeleton variant="text" width="60%" />
    </div>
  );
}

type LinkCardProps = {
  imageUrl: string | undefined | null;
  url: string;
  defaultImageUrl: string;
  label: string;
};

export function LinkCard({
  imageUrl,
  defaultImageUrl,
  label,
  url,
}: LinkCardProps) {
  const analytics = useAnalytics();
  const { trackEngagement } = useTrackEngagement();
  const finalImageUrl = imageUrl || defaultImageUrl;

  return (
    <Card
      className={cx(styles.listItem)}
      onClick={() => {
        analytics.track("Linked Clicked", { label, url });
        trackEngagement?.({ event: "Link Content Card Clicked" });
      }}
    >
      <CardActionArea href={url} target="_blank" rel="noopener noreferrer">
        <CardMedia
          image={finalImageUrl}
          title={`${label} Preview`}
          className={cx({
            [styles.listItemImage]: !!imageUrl,
            [styles.defaultImage]: !imageUrl,
          })}
        />
        <CardContent>
          <Typography variant="h6" noWrap>
            {label}
          </Typography>
          <Typography className={cx(styles.url)} noWrap>
            {url}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

type LinkItemProps = {
  url: string;
  imageUrl?: string;
  defaultImageUrl: string;
  label: string | undefined | null;
};

function LinkItem({ defaultImageUrl, imageUrl, label, url }: LinkItemProps) {
  const { data, loading } = useQuery<GetLinkPreview, GetLinkPreviewVariables>(
    PRISMA_GET_LINK_PREVIEW,
    {
      variables: { url },
      skip: !!imageUrl,
    }
  );

  return imageUrl || !loading ? (
    <LinkCard
      defaultImageUrl={defaultImageUrl}
      imageUrl={imageUrl || data?.getLinkPreview?.image}
      url={url}
      label={label || data?.getLinkPreview?.title || url}
    />
  ) : (
    <LinkLoading />
  );
}

export default LinkItem;
